import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { TlkModalWindowRef, TLK_MODAL_WINDOW_DATA } from '@itero/ui-toolkit-angular/modal-window';
import { SettingsService } from '../settings.service';
import { GlobalSettingsService } from '../../core/globalSettings.service';
import { KeyValuePair } from '../../shared/generalInterfaces';
import { Subscription } from 'rxjs';
import { ExportRequest } from '../orthoCadExport/orthoCadExport.service';
import { DownloadNotificationService } from '../../shared/downloadNotification/downloadNotification.service';
import { DownloadFileInfo, ExportInfoDto, CreateRestorativeExportFileRequestDto } from '../downloadNotification/fileInfo.model';
import { ISettingsData } from '../../interfaces/ISettingsData';
import { SoftwareOptionsForCompany } from '../enums';
import { SoftwareOptionsService } from 'app/core/companySoftwareOptions/service/softwareOptions.service';
import { TimberService } from '@logging/timber.service';
import { BiAction, BiObjectType } from '@logging/interfaces/bi.enums';

export interface RestorativeExportModalData {
	caseTypeId: number;
	orderId: number;
	orderDetailsId: number;
}

@Component({
	selector: 'eup-restorativeExport',
	templateUrl: './restorativeExport.component.html',
	styleUrls: [ './restorativeExport.component.scss' ],
	host: {
		'id': 'modal-restorativeExport'
	}
})
export class RestorativeExportModal implements OnInit {
	caseTypeId: number;
	orderId: number;
	orderDetailsId: number;
	hidePatientName: boolean;
	disablePatientDetailsBySWOpiont: boolean;

	restorativeExportFileRequest: CreateRestorativeExportFileRequestDto = new CreateRestorativeExportFileRequestDto();
	cadCamSystemTypeOptions: KeyValuePair[] = null;
	selectedCadCamSystemType: KeyValuePair | null = null;

	getOptionName = (option: KeyValuePair): string => option.value;
	compareWith = (v1: KeyValuePair, v2: KeyValuePair): boolean => v1?.key === v2?.key;

	exportSubscription: Subscription;
	exportWoSubscription: Subscription;

	private cachedSettings: ISettingsData | null = null;

	constructor(
		private settingsService: SettingsService,
		private globalSettings: GlobalSettingsService,
		private downloadNotificationService: DownloadNotificationService,
		private softwareOptionsService: SoftwareOptionsService,
		private timberService: TimberService,
		private cdr: ChangeDetectorRef,
		public modalWindowRef: TlkModalWindowRef<void>,
		@Inject(TLK_MODAL_WINDOW_DATA) public data: RestorativeExportModalData
	) {}

	ngOnInit(): void {
		// Initialize from modal data
		this.caseTypeId = this.data.caseTypeId;
		this.orderId = this.data.orderId;
		this.orderDetailsId = this.data.orderDetailsId;

		this.disablePatientDetailsBySWOpiont = this.softwareOptionsService.isSoftwareOptionExists(
			SoftwareOptionsForCompany.shouldDisableHidePatientDetails,
			this.globalSettings.get().companySoftwareOptions
		);

		this.loadSettings();
	}

	private loadSettings(): void {
		this.settingsService
			.getSettings(this.globalSettings.get().selectedCompanyId, true, false)
			.subscribe((settingsRes: ISettingsData) => {
				this.cachedSettings = settingsRes;
				this.applySettings(settingsRes);
			});
	}



	private applySettings(settingsRes: ISettingsData): void {
		this.hidePatientName = settingsRes.userSettings.patientAnonymization;
		this.cadCamSystemTypeOptions = settingsRes.cadCamSystemTypes.filter((o) => +o.key > 0);
		this.selectedCadCamSystemType = null;
		if (settingsRes.userSettings.exportCadCamSystemType) {
			const match = this.cadCamSystemTypeOptions.find(
				(o) => o.key.toString() === settingsRes.userSettings.exportCadCamSystemType.toString()
			);
			this.selectedCadCamSystemType = match ?? null;
		}
	}

  	export(): void {
		if (this.canExport()) {
			this.restorativeExportFileRequest.orderId = this.orderId;
			this.restorativeExportFileRequest.orderDetailsId = this.orderDetailsId;
			this.restorativeExportFileRequest.cadCamSystemTypeId = +this.selectedCadCamSystemType!.key;

			let settings = this.globalSettings.get();
			var requestData = this.createRequestData(settings.contactId);

			this.exportWoSubscription = this.downloadNotificationService
			.getRestorativeExportInfo(requestData)
			.subscribe((res: ExportInfoDto) => {
				if (res && res.isFileExists) {
					const fileInfo: DownloadFileInfo = {
						orderId: requestData.orderId,
						orderDetailsId: requestData.orderDetailsId,
						downloaded: true,
						requestData: requestData
					};
					this.downloadNotificationService.downloadFile(fileInfo);
				} else {
					this.downloadNotificationService.addFile(requestData);
				}
			});
			
			this.modalWindowRef.close();
		}
	}

	close(): void {
		this.modalWindowRef.close();
	}

	createRequestData(clientId: number): CreateRestorativeExportFileRequestDto {
		var requestData = new CreateRestorativeExportFileRequestDto();
		requestData.clientId = clientId;
		requestData.orderId = this.restorativeExportFileRequest.orderId;
		requestData.orderDetailsId = this.restorativeExportFileRequest.orderDetailsId;
		requestData.cadCamSystemTypeId = this.restorativeExportFileRequest.cadCamSystemTypeId;
		requestData.patientAnonymization = this.disablePatientDetailsBySWOpiont ? false : this.hidePatientName;
		var today = new Date();
		requestData.creationTime = today.toUTCString();

		return requestData;
	}

	onCadCamSystemChange(option: KeyValuePair): void {
		this.selectedCadCamSystemType = option ?? null;
		this.logDropdownChange('CadCamSystemDropdown', option?.value);
	}

	private logDropdownChange(objectName: string, selectedValue: string | undefined): void {
		this.timberService.userActionEvent({
			locationParentObject: 'RestorativeExportModal',
			objectName,
			objectType: BiObjectType.Select,
			action: selectedValue ? BiAction.Select : BiAction.Deselect,
			selectedValue,
		});
	}

	canExport(): boolean {
		return this.selectedCadCamSystemType != null && +this.selectedCadCamSystemType.key > 0;
	}

	onHidePatientNameChanged(newVal: boolean) {
		this.hidePatientName = newVal;
	}
}
